/**
 * Auth API — Layer 1 of the three-layer pattern.
 *
 * Pure async functions, no TanStack, no React imports.
 * Paths use AUTH (/api/v1/auth/) and PUBLISHERS (/api/v1/publishers/) constants.
 */
import { apiClient } from "@/shared/lib/axios";
import { AUTH, PUBLISHERS } from "@/shared/lib/apiPaths";
import type {
  LoginInput,
  LoginResponse,
  RegisterInput,
  RegisterResponse,
} from "../types";
import type { MeResponse } from "@/shared/types/user";

/**
 * POST /auth/login/
 *
 * Flow: validate credentials → return short-lived access token.
 * Refresh token is set as HttpOnly cookie by the backend.
 */
export async function login(input: LoginInput, idempotencyKey?: string): Promise<LoginResponse> {
  const { data } = await apiClient.post<LoginResponse>(`${AUTH}/login`, input, {
    headers: idempotencyKey ? { "Idempotency-Key": idempotencyKey } : undefined,
  });
  return data;
}

/**
 * POST /auth/register/
 *
 * Flow: create user + publisher + membership → send verification email.
 * Always returns 201 even on duplicate email (no enumeration).
 */
export async function register(input: RegisterInput, idempotencyKey?: string): Promise<RegisterResponse> {
  const { data } = await apiClient.post<RegisterResponse>(`${PUBLISHERS}/register`, input, {
    headers: idempotencyKey ? { "Idempotency-Key": idempotencyKey } : undefined,
  });
  return data;
}

/**
 * POST /auth/logout/
 *
 * Flow: blacklist refresh token cookie → clear cookie.
 * Idempotent — returns 204 even if already logged out.
 */
export async function logout(): Promise<void> {
  await apiClient.post(`${AUTH}/logout`);
}

/**
 * POST /auth/token/refresh/
 *
 * Flow: read HttpOnly refresh cookie → rotate tokens → return new access.
 * Used internally by the axios interceptor on 401. Exported for cases where
 * manual refresh is needed before a sensitive mutation.
 */
export async function refreshToken(): Promise<{ access: string }> {
  const { data } = await apiClient.post<{ access: string }>(
    `${AUTH}/token/refresh`,
    {},
    { withCredentials: true }
  );
  return data;
}

/**
 * POST /publishers/verify-email-otp/ — validate 6-digit OTP for email verification.
 *
 * Flow: validate OTP → advance accountStatus to pendingApproval.
 * Throws on invalid or expired OTP.
 */
export async function verifyEmailOtp(email: string, otp: string): Promise<void> {
  await apiClient.post(`${PUBLISHERS}/verify-email-otp`, { email, otp });
}

/**
 * POST /publishers/resend-verification/ — resend email verification OTP.
 *
 * Always returns 200 (no enumeration).
 */
export async function resendEmailVerificationOtp(email: string): Promise<void> {
  await apiClient.post(`${PUBLISHERS}/resend-verification`, { email });
}

/**
 * POST /auth/forgot-password/ — send (or resend) a password-reset OTP to the given email.
 */
export async function forgotPassword(email: string): Promise<void> {
  await apiClient.post(`${AUTH}/forgot-password`, { email });
}

/**
 * POST /auth/verify-otp/ — check if a 6-digit OTP is valid for a given email.
 *
 * Flow: check if OTP exists in cache for email → return validity.
 * Does NOT consume or clear the OTP — the actual reset must call reset-password.
 *
 * @returns {{ valid: boolean }} — valid=false means wrong or expired OTP.
 * Throws on network/validation errors.
 */
export async function verifyOtp(email: string, otp: string): Promise<{ valid: boolean }> {
  const { data } = await apiClient.post<{ valid: boolean }>(
    `${AUTH}/verify-otp`,
    { email, otp }
  );
  return data;
}

/**
 * POST /auth/reset-password/ — validate OTP and set new password.
 */
export async function resetPassword(email: string, otp: string, newPassword: string): Promise<void> {
  await apiClient.post(`${AUTH}/reset-password`, {
    email,
    otp,
    newPassword,
  });
}

/**
 * PATCH /auth/photo/ — upload a new profile photo.
 *
 * Sends multipart/form-data. Backend resizes to 300×300 JPEG and stores in S3.
 * Returns a presigned URL for the new photo.
 */
export async function uploadPhoto(file: File): Promise<{ photoUrl: string }> {
  const form = new FormData();
  form.append("photo", file);
  const { data } = await apiClient.patch<{ photoUrl: string }>(
    `${AUTH}/photo`,
    form,
    { headers: { "Content-Type": "multipart/form-data" } }
  );
  return data;
}

/** DELETE /auth/photo/ — remove the current profile photo. */
export async function deletePhoto(): Promise<void> {
  await apiClient.delete(`${AUTH}/photo`);
}

/** GET /auth/me/ — return current user identity including publisher profile. */
export async function getMe(): Promise<MeResponse> {
  const { data } = await apiClient.get<MeResponse>(`${AUTH}/me`);
  return data;
}

/**
 * PATCH /auth/me/ — update the authenticated user's full name and/or email.
 *
 * Backend persists the changes and returns the refreshed identity payload.
 * Both fields are JWT claims, so callers should rotate the access token
 * afterwards (see useUpdateProfile) for token-derived state to reflect them.
 */
export async function updateMe(payload: { fullName?: string; email?: string }): Promise<MeResponse> {
  const { data } = await apiClient.patch<MeResponse>(`${AUTH}/me`, payload);
  return data;
}

export { changePassword } from "./changePasswordApi";